import { useMemo } from 'react';
import {
    FacilityVisitEntryDto,
    WorkoutEntryDto,
    WorkoutTypeV2
} from '../../../../core/api/FitnessActivityClient';
import { getColorForId } from './GpxMap.utils';
import {
    TimeframeData,
    UseTimeframeDataProps,
    ChartSeries
} from './TimeframeChart.types';

export function useTimeframeData({
    data
}: UseTimeframeDataProps): TimeframeData[] {
    return useMemo(() => {
        if (!data) return [];
        const timeframeData: TimeframeData[] = [];

        if (data.workouts) {
            data.workouts.forEach((workout: WorkoutEntryDto) => {
                const timestamp = workout.workoutDateTimeRange;
                if (timestamp && timestamp.from && timestamp.to) {
                    timeframeData.push({
                        id: `${workout.id}`,
                        workoutType: workout.workoutType || WorkoutTypeV2.OTHER,
                        startTime: new Date(timestamp.from),
                        endTime: new Date(timestamp.to)
                    });
                }
            });
        }

        if (data.facilityVisits) {
            data.facilityVisits.forEach((visit: FacilityVisitEntryDto) => {
                const timestamp = visit.facilityVisitEntryDateTimeRange;
                if (timestamp && timestamp.from && timestamp.to) {
                    timeframeData.push({
                        id: `${visit.id}`,
                        workoutType: WorkoutTypeV2.OTHER,
                        startTime: new Date(timestamp.from),
                        endTime: new Date(timestamp.to),
                        facilityName: visit.facilityName
                    });
                }
            });
        }

        return timeframeData.sort(
            (a, b) => a.startTime.getTime() - b.startTime.getTime()
        );
    }, [data]);
}

export const formatTime = (date: Date): string =>
    `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

export function buildChartSeries(
    timeframeData: TimeframeData[],
    xAxisData: Date[]
): ChartSeries[] {
    return timeframeData.map((activity, idx) => {
        const label = activity.facilityName || activity.workoutType;
        const yValue = idx + 1;
        const data = xAxisData.map((time) => {
            const t = time.getTime();
            return t >= activity.startTime.getTime() &&
                t <= activity.endTime.getTime()
                ? yValue
                : null;
        });
        const timeRange = `${formatTime(activity.startTime)} - ${formatTime(activity.endTime)}`;
        return {
            data,
            label,
            color: getColorForId(activity.id),
            showMark: false,
            connectNulls: false,
            valueFormatter: (v: number | null) =>
                v !== null ? timeRange : null
        };
    });
}

export function generateXAxisData(timeframeData: TimeframeData[]): Date[] {
    const timestamps = new Set<number>();
    timeframeData.forEach((t) => {
        let current = t.startTime.getTime();
        const end = t.endTime.getTime();
        while (current <= end) {
            timestamps.add(current);
            current += 60 * 1000;
        }
        timestamps.add(end);
    });
    return Array.from(timestamps)
        .sort((a, b) => a - b)
        .map((t) => new Date(t));
}

export const getDayBounds = (userDayDate: Date): { min: Date; max: Date } => ({
    min: new Date(
        Date.UTC(
            userDayDate.getUTCFullYear(),
            userDayDate.getUTCMonth(),
            userDayDate.getUTCDate(),
            0,
            0,
            0
        )
    ),
    max: new Date(
        Date.UTC(
            userDayDate.getUTCFullYear(),
            userDayDate.getUTCMonth(),
            userDayDate.getUTCDate(),
            23,
            59,
            59
        )
    )
});
